// Display-only formatting helpers for the (RTL Persian) UI.
// IMPORTANT: these are for DISPLAY only - never feed the result back into logic,
// an <input value>, a URL, or anything compared/sent to the server.

const FA_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

// Convert every ASCII digit in a value to its Persian digit (۰-۹).
export function toFa(v) {
  if (v === null || v === undefined) return v;
  return String(v).replace(/[0-9]/g, (d) => FA_DIGITS[Number(d)]);
}

// Fold Persian (۰-۹) and Arabic-Indic (٠-٩) digits back to ASCII - for parsing
// what a user typed in a numeric field that DISPLAYS Persian digits.
export function toEn(v) {
  if (v === null || v === undefined) return v;
  return String(v)
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06F0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));
}

// Known abbreviations that should render fully UPPER-CASE in the UI.
const ABBR = new Set([
  'fat', 'odc', 'hh', 'olt', 'onu', 'ont', 'odf', 'fttx', 'ftth', 'gpon',
  'sld', 'ddr', 'kmz', 'bom', 'ai', 'gps', 'sms', 'otp', 'api', 'pwa', 'url', 'id', 'tc',
]);

// Title-case English UI tokens: a known abbreviation -> UPPER-CASE, a plain word
// -> PascalCase. Separators (space / - _ , ،) and non-Latin tokens pass through
// untouched, so "site-entry" -> "Site-Entry", "fat" -> "FAT", "before" -> "Before",
// "t-connector" -> "T-Connector", and Persian text is never altered.
export function formatEn(s) {
  if (s === null || s === undefined) return s;
  return String(s).split(/([\s/_,،-]+)/).map((tok) => {
    if (!tok || /^[\s/_,،-]+$/.test(tok)) return tok;
    const low = tok.toLowerCase();
    if (ABBR.has(low)) return tok.toUpperCase();
    if (/^[a-zA-Z]+$/.test(tok)) return tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase();
    return tok; // non-Latin or mixed -> leave as-is
  }).join('');
}

// Persian labels for the system role slugs (nicer than English in an RTL panel).
const ROLE_FA = {
  admin: 'مدیر', super_admin: 'مدیر ارشد', catalogue_admin: 'مدیر کاتالوگ',
  coverage_manager: 'مدیر پوشش', operator: 'کارشناس', supervisor: 'ناظر', design_approver: 'تأیید تیم طراحی اپراتور',
  sales_manager: 'مدیر فروش', sales_agent: 'کارشناس فروش', catalog_manager: 'مدیر کالا',
  // Wording taken verbatim from section 3.1 of the customer requirements
  // document rather than invented, so the panel reads back the same words the
  // owner used. `نماینده فروش` there is our existing sales_agent.
  sales_director: 'مدیر کل فروش', sales_supervisor: 'سرپرست فروش',
  sales_finance: 'مسئول مالی نمایندگان', sales_auditor: 'ناظر فروش',
  content_editor: 'ویراستار محتوا', seo_manager: 'مدیر سئو',
  marketing_manager: 'مدیر بازاریابی', marketer: 'کارشناس بازاریابی',
  finance_manager: 'مدیر مالی', accountant: 'حسابدار',
  support_manager: 'مدیر پشتیبانی', support_agent: 'کارشناس پشتیبانی',
  compliance_manager: 'مدیر انطباق', analyst: 'تحلیل‌گر', auditor: 'حسابرس',
  contractor: 'شرکت پیمانکار', installer: 'نیروی اجرایی', customer: 'مشتری',
};
export function faRole(role) { return ROLE_FA[role] || formatEn(role); }
